// src/pages/billing/InvoiceList.js

import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";

export default function InvoiceList() {
  const user       = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";
  const navigate   = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [search,   setSearch]   = useState("");
  const [filter,   setFilter]   = useState("All");
  const [loading,  setLoading]  = useState(true);

  const [summary, setSummary] = useState({
    totalRevenue: 0, paidRevenue: 0, pendingRevenue: 0, overdueCount: 0,
  });

  useEffect(() => { if (company_id) fetchInvoices(); }, [company_id]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const snap = await getDocs(
        query(collection(db, "invoices"), where("company_id", "==", company_id))
      );
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      let totalRevenue = 0, paidRevenue = 0, pendingRevenue = 0, overdueCount = 0;
      data.forEach(inv => {
        const total   = Number(inv.total_amount   || 0);
        const balance = Number(inv.balance_amount || 0);
        totalRevenue += total;
        if (inv.payment_status === "Paid")    paidRevenue    += total;
        if (inv.payment_status === "Pending" || inv.payment_status === "Partial")
          pendingRevenue += balance;
        if (inv.payment_status === "Overdue") overdueCount++;
      });

      data.sort((a, b) => (b.invoice_date?.seconds || 0) - (a.invoice_date?.seconds || 0));
      setInvoices(data);
      setSummary({ totalRevenue, paidRevenue, pendingRevenue, overdueCount });
    } catch (err) {
      console.error("Invoice fetch error:", err);
    } finally { setLoading(false); }
  };

  const filtered = invoices.filter(inv => {
    const matchSearch = !search ||
      inv.invoice_no?.toLowerCase().includes(search.toLowerCase()) ||
      inv.client_name?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "All" || inv.payment_status === filter;
    return matchSearch && matchFilter;
  });

  const statusBadge = (s) => ({
    Paid:    "bg-green-100 text-green-700",
    Pending: "bg-yellow-100 text-yellow-700",
    Partial: "bg-blue-100 text-blue-700",
    Overdue: "bg-red-100 text-red-700",
    Draft:   "bg-gray-100 text-gray-700",
  }[s] || "bg-gray-100 text-gray-700");

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Invoice List</h1>
        <button onClick={() => navigate("/billing/create")}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">
          + Create Invoice
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard title="Total Revenue"     value={`₹${summary.totalRevenue.toLocaleString()}`}   color="blue" />
        <SummaryCard title="Collected"         value={`₹${summary.paidRevenue.toLocaleString()}`}    color="green" />
        <SummaryCard title="Pending Collection" value={`₹${summary.pendingRevenue.toLocaleString()}`} color="yellow" />
        <SummaryCard title="Overdue"           value={summary.overdueCount}                          color="red" />
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <input placeholder="Search by invoice no or client..."
          className="border p-2 rounded-lg w-full md:w-72 text-sm"
          value={search} onChange={e => setSearch(e.target.value)} />
        <div className="flex gap-2 flex-wrap">
          {["All", "Paid", "Pending", "Partial", "Overdue", "Draft"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                filter === f
                  ? "bg-blue-600 text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}>{f}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        {loading ? (
          <p className="p-6 text-gray-500">Loading invoices...</p>
        ) : filtered.length === 0 ? (
          <p className="p-6 text-gray-500">No invoices found.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="p-3 text-left">Invoice No</th>
                <th className="p-3 text-left">Client</th>
                <th className="p-3 text-left">Project</th>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-right">Total ₹</th>
                <th className="p-3 text-right">Balance ₹</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => (
                <tr key={inv.id}
                  onClick={() => navigate(`/billing/invoice/${inv.id}`)}
                  className="border-b hover:bg-gray-50 cursor-pointer">
                  <td className="p-3 font-medium">{inv.invoice_no}</td>
                  <td className="p-3">{inv.client_name}</td>
                  <td className="p-3 text-gray-500">{inv.project_name || "-"}</td>
                  <td className="p-3 text-gray-500">
                    {inv.invoice_date?.seconds
                      ? new Date(inv.invoice_date.seconds * 1000).toLocaleDateString()
                      : "-"}
                  </td>
                  <td className="p-3 text-right">₹{Number(inv.total_amount   || 0).toLocaleString()}</td>
                  <td className="p-3 text-right font-medium text-red-600">
                    {Number(inv.balance_amount || 0) > 0
                      ? `₹${Number(inv.balance_amount).toLocaleString()}`
                      : "—"}
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusBadge(inv.payment_status)}`}>
                      {inv.payment_status}
                    </span>
                  </td>
                  <td className="p-3 space-x-3">
                    <button onClick={() => navigate(`/billing/invoice/${inv.id}`)}
                      className="text-blue-600 hover:underline text-xs">View</button>
                    <button onClick={() => navigate(`/billing/create?id=${inv.id}`)}
                      className="text-green-600 hover:underline text-xs">Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ title, value, color = "blue" }) {
  const colors = {
    blue:   "border-blue-200 bg-blue-50",
    green:  "border-green-200 bg-green-50",
    yellow: "border-yellow-200 bg-yellow-50",
    red:    "border-red-200 bg-red-50",
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <p className="text-xs text-gray-500 uppercase tracking-wide">{title}</p>
      <h3 className="text-xl font-bold mt-1">{value}</h3>
    </div>
  );
}
